import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { StyleEnums } from "models/enums";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import {
  DashboardButtonGroup,
  DialogActionButton,
  PageLayouts,
  RouteBreadCrumb,
  Skeletons,
  StaticInfoMessage,
  TableAsync,
} from "components/common";
import { Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";
import useAdvisorPermissions from "../usePermissions";
import MonthlyReportStatusTag from "./statusTag";
import { useCompanyInstructionDialog, useGenerateMonthlyReportDialog } from "./dialogs";
import { useMonthlyReportService } from "serviceHooks";
//#endregion
import { GENERATING_POLL_INTERVAL_MS, GENERATING_POLL_TIMEOUT_MS } from "./polling";
import { DateHelper, LocalizationHelperInstance, UrlHelper, UtilsHelper } from "utils/helpers";
import { IconDefinitionConstants } from "utils/constants";
import cssExports from "theme/styles/main.module.scss";

type Row = WintSuperkollAPIModel.MonthlyReportSummary;

// The service caps a page at 1000 rows, so the whole set always fits on one. The page is sized to
// that ceiling rather than to the current row count because `rowsPerPage` is seeded on mount only:
// a page sized to a count that later grew would start splitting the set, and the array branch of
// TableAsync sorts after it slices.
const SINGLE_PAGE_ROW_COUNT = 1000;

/**
 * The company's monthly reports — the advisor's entry point, mounted as a sub-page of
 * Rådgivningsvyn the way the documentation log is.
 *
 * list-page shape per the web guidelines: DashboardLayout + a top-right action cluster +
 * TableAsync, row click drilling into the report. No FilterButton yet — a company produces
 * roughly twelve reports a year, so a filter earns its place only once the list spans years.
 * The endpoint does accept a status filter when that day comes.
 */
const MonthlyReportsList: React.FC = () => {
  const navigate = useNavigate();
  const { advisorViewsAvailable } = useAdvisorPermissions();
  const { listReports } = useMonthlyReportService();

  const [reports, setReports] = React.useState<Row[]>();
  const [loadFailed, setLoadFailed] = React.useState<boolean>(false);

  const load = React.useCallback(
    () =>
      // The whole set in one request. `isAutoPaginating` puts every row in one page, which the
      // report service caps at 1000 — several decades of a company's reports.
      listReports({ page: 0, orderByDescending: false, thenByDescending: false, isAutoPaginating: true })
        .then((res) => {
          setReports(res.data.items ?? []);
          setLoadFailed(false);
        })
        // A refresh that fails leaves what is already on screen alone. A first load that fails says
        // so, rather than rendering an empty table: "no reports yet" and "we could not ask" look
        // identical to the advisor, and only one of them is a reason to generate one.
        .catch(() => setLoadFailed(true)),
    // Deps are empty: `listReports` is fresh each render, so including it would loop.
    []
  );

  React.useEffect(() => {
    load();
  }, [load]);

  // Generating a report leaves its row in Generating, and the advisor's natural move during a pass
  // that takes 15-90 seconds is to come back here and do something else. Without this the row would
  // sit in Generating until the page was reloaded, long after the report had become a draft.
  const isGenerating = !!reports?.some((r) => r.status === WintSuperkollAPIModel.MonthlyReportStatus.Generating);
  React.useEffect(() => {
    if (!isGenerating) return undefined;

    const startedWaitingAt = Date.now();
    const timer = window.setInterval(() => {
      // No notice when it gives up, unlike the report view: a row still reading Generating is what
      // the server last said, so the list stays truthful by simply going quiet.
      if (Date.now() - startedWaitingAt >= GENERATING_POLL_TIMEOUT_MS) {
        window.clearInterval(timer);
        return;
      }
      load();
    }, GENERATING_POLL_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [isGenerating, load]);

  const openReport = (id: string) => navigate(UrlHelper.formatPath(`/AdvisoryView/MonthlyReports/${id}`));
  const getGenerateDialog = useGenerateMonthlyReportDialog(openReport);
  const getCompanyInstructionDialog = useCompanyInstructionDialog();

  const formatDate = (date?: Date) => (date ? DateHelper.convertDateToShortISOString(date) : "—");

  return (
    <PageLayouts.DashboardLayout
      entityKey={0}
      hasPermissions={advisorViewsAvailable}
      title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.MonthlyReports)}
      // Passing `current` explicitly keeps the trail at "Rådgivning > Periodrapporter"; without it
      // the empty index child supplies the leaf and the crumb repeats itself.
      breadCrumb={
        <RouteBreadCrumb
          className={cssExports.dashBoardNavigation}
          current={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.MonthlyReports)}
        />
      }
      topRightButtons={
        <DashboardButtonGroup
          buttons={[
            <DialogActionButton
              size="sm"
              actionDialog={getGenerateDialog}
              reload={UtilsHelper.emptyFunc}
              testId="generateMonthlyReport"
              key="generateMonthlyReport"
            >
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.GenerateMonthlyReport)}
            </DialogActionButton>,
          ]}
          moreActions={[
            {
              menuKey: "companyInstruction",
              description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.CompanyInstruction),
              icon: IconDefinitionConstants.WSettings,
              actionDialog: getCompanyInstructionDialog(),
            },
          ]}
        />
      }
    >
      {/* The Grid item is the layout slot DashboardLayout's spacing container expects — the same
          wrapper report.tsx and the holding-register report put around their own TableAsync. */}
      <Grid container item>
        {loadFailed && reports === undefined ? (
          <StaticInfoMessage displayType={StyleEnums.DisplayType.Danger}>
            {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.MonthlyReportsLoadFailed)}
          </StaticInfoMessage>
        ) : reports === undefined ? (
          <Skeletons.TableSkeleton />
        ) : (
          <TableAsync<Row>
            tableId="advisorMonthlyReports"
            // The rows are handed over as an array rather than as a fetch function, because the
            // endpoint answers newest period first and ignores ordering properties. In fetch mode a
            // clicked column header would send `orderByProperty`, get the same order back, and move
            // nothing but the arrow. With an array TableAsync sorts client-side instead.
            //
            // That only holds on a single page: the array branch slices to the current page *before*
            // it sorts, so a split set would sort only the rows already in view, which looks like it
            // worked. Keeping everything on one page is what makes the sort honest while the list
            // keeps the pagination footer the list-page shape calls for.
            filter={reports}
            maxRowCount={SINGLE_PAGE_ROW_COUNT}
            // Both the page index and the rows-per-page are read from the URL otherwise, so a
            // bookmarked `advisorMonthlyReports_rowsPerPage` would split the set again from behind.
            // The same flag drops the sort out of the URL, which a list this size can afford.
            disableQueryPagination
            // By the last month, so a quarter report sits next to the month report it ends with.
            orderByProperty="periodEnd"
            orderByDescending
            rowClickHandler={(row: Row) => row.id && openReport(row.id)}
            properties={{
              periodEnd: {
                description: TranslationKeys.Advisor.MonthlyReportPeriod,
                noWrap: true,
                formatter: (r: Row) => `${formatDate(r.periodStart)} – ${formatDate(r.periodEnd)}`,
              },
              status: {
                description: TranslationKeys.Common.Status,
                noWrap: true,
                formatter: (r: Row) => <MonthlyReportStatusTag status={r.status} />,
              },
              approvedAt: {
                description: TranslationKeys.Advisor.MonthlyReportSharedAt,
                noWrap: true,
                formatter: (r: Row) => formatDate(r.approvedAt),
              },
              // Moves on content changes only (generate, regenerate, edit); approving leaves it
              // alone, which the shared column already shows. Null on reports from before the
              // service tracked it, so those read "—" rather than an invented date.
              updatedAt: {
                description: TranslationKeys.Advisor.MonthlyReportUpdatedAt,
                noWrap: true,
                formatter: (r: Row) => formatDate(r.updatedAt),
              },
            }}
          />
        )}
      </Grid>
    </PageLayouts.DashboardLayout>
  );
};

export default MonthlyReportsList;
